import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";
import LuminartLogo from "./LuminartLogo";
import { useSiteText } from "../lib/content";

const ease = [0.16, 1, 0.3, 1];

const CAPABILITIES = [
  {
    title: "Site Survey & Scouting",
    desc: "We assess each location for visibility, projection surfaces and technical feasibility. For advertising campaigns, we identify high-impact locations and the best surfaces to maximise audience reach.",
  },
  {
    title: "Planning, Permits & Licensing",
    desc: "We handle permits, permissions and local requirements from start to finish, making sure every project is properly planned and ready to go.",
  },
  {
    title: "Concept & Creative Development",
    desc: "We help develop ideas from the first concept to the final creative direction, working with agencies and brands to make the most of light, architecture and space.",
  },
  {
    title: "3D Content & Animation",
    desc: "Our in-house creative team produces 2D, 3D, motion graphics and animation tailored to the projection surface, campaign and creative brief.",
  },
  {
    title: "High-Brightness Projection Systems",
    desc: "We provide high-brightness projection systems using the latest projection technology, with high-resolution and 4K-enhanced imaging for powerful, precise visuals in demanding environments.",
  },
  {
    title: "Live Event Operation",
    desc: "Our technical teams can operate and manage projection systems for any production, from setup and testing through to live operation and troubleshooting.",
  },
  {
    title: "Content Capture & Amplification",
    desc: "Our photographers and drone teams capture each projection and turn it around quickly with professionally edited photo and video content, ready for social media, press and campaign communications.",
  },
  {
    title: "Worldwide Logistics & Crew",
    desc: "We coordinate equipment, transport, logistics and experienced technical crew across international locations, giving you one partner from planning through to execution.",
  },
];

export const Capabilities = () => {
  const [open, setOpen] = useState(null);
  const t = useSiteText();

  return (
    <section
      id="process"
      data-testid="capabilities-section"
      className="relative bg-[#0a0a0c] border-t border-white/5 py-20 md:py-28 px-6 md:px-12 lg:px-16 overflow-hidden"
    >
      {/* Geometric LP motif */}
      <LuminartLogo
        className="pointer-events-none absolute -right-24 top-1/2 -translate-y-1/2 w-[30rem] opacity-[0.04]"
        glow={false}
      />

      <div className="relative">
        <div id="capabilities">
          <span className="text-[11px] uppercase tracking-[0.3em] text-white/85">
            {t("capabilities_label", "Capabilities")}
          </span>
          <div className="mt-2 h-px w-9 bg-[#c8b99d]" />
          <p className="mt-6 max-w-md text-sm font-light leading-relaxed text-white/45">
            {t(
              "capabilities_intro",
              "High brightness projectors, media servers and industry-leading software — operated entirely in-house."
            )}
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-x-16 max-w-4xl">
          {CAPABILITIES.map((c, i) => {
            const isOpen = open === i;
            return (
              <motion.div
                key={c.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.8, delay: (i % 2) * 0.06, ease }}
                className="border-t border-white/10"
              >
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? null : i)}
                  data-testid={`capability-item-${i}`}
                  aria-expanded={isOpen}
                  className="group flex w-full items-center justify-between py-5 text-left cursor-pointer"
                >
                  <div className="flex items-baseline gap-5">
                    <span className="text-[10px] tabular-nums tracking-[0.25em] text-white/25">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span
                      className={`text-base md:text-lg font-light transition-colors duration-500 ${
                        isOpen ? "text-white" : "text-white/70 group-hover:text-white"
                      }`}
                    >
                      {c.title}
                    </span>
                  </div>
                  <motion.span
                    animate={{ rotate: isOpen ? 45 : 0 }}
                    transition={{ duration: 0.4, ease }}
                    className={`shrink-0 transition-colors duration-500 ${
                      isOpen ? "text-[#c8b99d]" : "text-white/30 group-hover:text-white/70"
                    }`}
                  >
                    <Plus size={15} strokeWidth={1.25} />
                  </motion.span>
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      key="desc"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.45, ease }}
                      className="overflow-hidden"
                    >
                      <p
                        data-testid={`capability-desc-${i}`}
                        className="pb-6 md:pl-10 text-sm font-light leading-relaxed text-white/45 max-w-lg"
                      >
                        {c.desc}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Capabilities;
